import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MixingWork, FileRecord } from '@twinsketch/topika-model';
import ErrorModal from 'src/components/common/ErrorModal';
import 'src/styles/workOrder/work-order.css';

interface WorkOrderMixingWorkFormProps {
  mixingWorks: MixingWork[];
  files: FileRecord[];
}

const WorkOrderMixingWorkForm: React.FC<WorkOrderMixingWorkFormProps> = ({
  mixingWorks,
  files,
}) => {
  const { id } = useParams<{ id?: string }>();
  const [error, setError] = useState<string | null>(null);
  const [expandedIndices, setExpandedIndices] = useState<Set<number>>(new Set());

  const sortedMixingWorks = useMemo(() => {
    return [...mixingWorks].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateB - dateA; // descending (latest first)
    });
  }, [mixingWorks]);

  const toggleExpand = (index: number) => {
    setExpandedIndices(prev => {
      const updated = new Set(prev);
      if (updated.has(index)) {
        updated.delete(index);
      } else {
        updated.add(index);
      }
      return updated;
    });
  };

  return (
    <div className="content-wrapper">
      <ErrorModal message={error} onClose={() => setError(null)} />

      <h2>Mixing Summary</h2>
      <div className="table-wrapper">
        <table className="mixing-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th></th>
              <th>Date</th>
              <th>Operators</th>
              <th>Project</th>
              <th>Lot No</th>
              <th>Tank Capacity (L)</th>
              <th>Grind Test (㎛)</th>
              <th>Viscosity (cps)</th>
              <th>Measured Solid Content (%)</th>
              <th>Calculated Solid Content (%)</th>
              <th>Ref. Filename</th>
            </tr>
          </thead>
          <tbody>
            {sortedMixingWorks.length > 0 ? (
              sortedMixingWorks.map((work, index) => {
                const extractedFrom = work.extractedFrom;
                const matchedFile = extractedFrom?.sourceFileId
                  ? files.find(file => file.id === extractedFrom?.sourceFileId)
                  : null;

                return (
                  <React.Fragment key={index}>
                    <tr>
                      <td>
                        <button
                          onClick={() => toggleExpand(index)}
                          aria-label={`Toggle details for entry ${index + 1}`}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            color: 'brown',
                          }}
                        >
                          {expandedIndices.has(index) ? '−' : '+'}
                        </button>
                      </td>
                      <td>{work.date ?? '—'}</td>
                      <td>{work.operators?.join(' | ') ?? '—'}</td>
                      <td>{work.projectName ?? '—'}</td>
                      <td>{work.lotNumber ?? '—'}</td>
                      <td>{work.tankVolumeLiters ?? '—'}</td>
                      <td>{work.grindSizeMicrons ?? '—'}</td>
                      <td>{work.measuredViscosityCps ?? '—'}</td>
                      <td>{work.measuredSolidContentPercent ?? '—'}</td>
                      <td>{work.calculatedSolidContentPercent ?? '—'}</td>
                      <td>
                        {matchedFile ? (
                          <span style={{ color: 'green' }}>{matchedFile.fileName}</span>
                        ) : (
                          <span style={{ color: 'gray' }}>No File Available</span>
                        )}
                      </td>
                    </tr>

                    {expandedIndices.has(index) && (
                      <tr>
                        <td colSpan={11} style={{ padding: 0 }}>
                          <div
                            style={{
                              padding: '12px',
                              backgroundColor: '#fafafa',
                              borderTop: '1px solid #ccc',
                            }}
                          >
                            <strong style={{ display: 'block', marginBottom: '8px' }}>
                              Mixing Steps
                            </strong>
                            <div>
                              <table>
                                <thead>
                                  <tr>
                                    <th>Step</th>
                                    <th>Material</th>
                                    <th>Amount (kg)</th>
                                    <th>RPM (Low)</th>
                                    <th>RPM (High)</th>
                                    <th>Duration (min)</th>
                                    <th>Remarks</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {work.steps?.map((step, sIndex) => (
                                    <tr key={sIndex}>
                                      <td>{step.stepOrder}</td>
                                      <td>{step.materialName}</td>
                                      <td>{step.amountInKg ?? '—'}</td>
                                      <td>{step.rpmLow ?? '—'}</td>
                                      <td>{step.rpmHigh ?? '—'}</td>
                                      <td>{step.durationMinutes ?? '—'}</td>
                                      <td>{step.remarks ?? '—'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={12} style={{ textAlign: 'center', color: 'gray' }}>
                  No Mixing Work Available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkOrderMixingWorkForm;
